/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "Share profile" modal — Figma Ping Android Dark 6661:6518. Header
// (Cancel · Share profile · Done) + "Copy profile link" row + contact search +
// multi-select contacts list. "Done" shares the profile with the selected
// contacts. Rendered via the shared `AnchoredPopover` (web body portal / native
// Modal) anchored to the Share button — opens on the first click and keeps the
// search input focusable inside the parent Chat Info modal via `trapFocus`.

import { Check, Link2, Search, X } from "lucide-react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, Text, TextInput, View } from "react-native";
import { ProfileAvatar } from "../profile/ProfileParts";
import { AnchoredPopover } from "../components/AnchoredPopover";

export interface ShareProfileContact {
  readonly id: string;
  readonly name: string;
  readonly avatarUri?: string | undefined;
  /** Account id of the person behind this row. For a 1:1 contact this is the
   *  peer's account id; for a directory-search result it's that user's account
   *  id. Lets the host add a searched user to a group / start a 1:1 with them
   *  even when there is no pre-existing conversation. */
  readonly accountId?: string | undefined;
  /** Secondary line (role · company, or "@handle") shown under the name —
   *  populated for directory-search results. */
  readonly subtitle?: string | undefined;
  /** Bucket for the section header: "contact" (existing chats) vs "on-ping"
   *  (directory-search match). Defaults to "contact". */
  readonly source?: "contact" | "on-ping" | undefined;
}

/** Result of a share-to-contacts action, surfaced inline in the modal so the
 *  user gets real confirmation (the chat room's toast is a no-op by design). */
export interface ShareResult {
  readonly sent: number;
  readonly failed: number;
}

export interface ShareProfileModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Ref to the trigger (Share button). `AnchoredPopover` anchors the panel
   *  below it (flipping above when there's no room) and measures it on open. */
  readonly anchorRef: { current: unknown };
  /** Contacts/conversations the profile can be shared to. */
  readonly contacts: readonly ShareProfileContact[];
  /** Copy the profile link to the clipboard. */
  readonly onCopyLink?: (() => void) | undefined;
  /** Share the profile to the selected contacts (on "Done"). Receives the full
   *  selected `ShareProfileContact` objects (not just ids) so the host can
   *  resolve directory-searched users that have no pre-existing conversation.
   *  May return a Promise<ShareResult> so the modal can show real "Sending… /
   *  Sent to N / Couldn't send" feedback and auto-close on success. */
  readonly onShare?:
    | ((contacts: readonly ShareProfileContact[]) => void | Promise<ShareResult>)
    | undefined;
  /** Directory search (like NewChatModal). When provided, a non-empty query
   *  searches the server directory and merges the matches in below the local
   *  contacts, so any Ping user — not just existing chats — can be shared to /
   *  invited. `signal` cancels stale requests. */
  readonly onSearchUsers?:
    | ((query: string, signal: AbortSignal) => Promise<readonly ShareProfileContact[]>)
    | undefined;
  /** Override the panel title (default "Share profile"). */
  readonly title?: string | undefined;
  /** Override the link-copy row label (default "Copy profile link"). */
  readonly copyLinkLabel?: string | undefined;
  /** Override the confirm-button label (default "Done"). */
  readonly doneLabel?: string | undefined;
}

export function ShareProfileModal({
  open,
  onClose,
  anchorRef,
  contacts,
  onCopyLink,
  onShare,
  onSearchUsers,
  title = "Share profile",
  copyLinkLabel = "Copy profile link",
  doneLabel = "Done",
}: ShareProfileModalProps): JSX.Element | null {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [copied, setCopied] = useState(false);
  // Directory-search state (only used when `onSearchUsers` is provided).
  const [searchResults, setSearchResults] = useState<readonly ShareProfileContact[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Share progress/result — drives the inline banner + Done button state.
  const [shareState, setShareState] = useState<
    { kind: "idle" } | { kind: "sending" } | { kind: "done"; result: ShareResult } | { kind: "error" }
  >({ kind: "idle" });
  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelected(new Set());
      setCopied(false);
      setSearchResults([]);
      setSearchError(null);
      setShareState({ kind: "idle" });
      abortRef.current?.abort();
    }
  }, [open]);

  // Debounced directory search — mirrors NewChatModal (450ms gate + AbortController
  // so a slow response can't land after a newer query). No-op without `onSearchUsers`.
  useEffect(() => {
    if (!open || onSearchUsers === undefined) return;
    const q = query.trim();
    if (q.length === 0) {
      setSearchResults([]);
      setSearchError(null);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    const t = setTimeout(() => {
      setSearching(true);
      setSearchError(null);
      void onSearchUsers(q, controller.signal)
        .then((users) => {
          if (!controller.signal.aborted) setSearchResults(users);
        })
        .catch(() => {
          if (!controller.signal.aborted) setSearchError("Couldn't search. Try again.");
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, 450);
    return () => {
      controller.abort();
      clearTimeout(t);
    };
  }, [query, open, onSearchUsers]);
  // Inline "Copied!" confirmation — the chat room suppresses toasts by product
  // direction, so the copy row gives its own feedback.
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = (): void => {
    onCopyLink?.();
    setCopied(true);
  };

  // Local contacts filtered by the query (name substring). When a query is
  // active these are the "Your chats" bucket.
  const localFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return contacts;
    return contacts.filter((c) => c.name.toLowerCase().includes(q));
  }, [contacts, query]);

  // Directory matches that AREN'T already a local contact — deduped by
  // accountId first (same person, different row), then by id.
  const directoryOnly = useMemo(() => {
    if (searchResults.length === 0) return [] as readonly ShareProfileContact[];
    const localAccountIds = new Set(
      contacts.map((c) => c.accountId).filter((a): a is string => typeof a === "string" && a.length > 0),
    );
    const localIds = new Set(contacts.map((c) => c.id));
    return searchResults.filter(
      (u) => !localIds.has(u.id) && !(u.accountId !== undefined && localAccountIds.has(u.accountId)),
    );
  }, [searchResults, contacts]);

  // Every row currently shown, keyed by id — lets `done()` hand the host the
  // full contact objects (with accountId) for the current selection.
  const rowsById = useMemo(() => {
    const map = new Map<string, ShareProfileContact>();
    for (const c of contacts) map.set(c.id, c);
    for (const c of localFiltered) map.set(c.id, c);
    for (const c of directoryOnly) map.set(c.id, c);
    return map;
  }, [contacts, localFiltered, directoryOnly]);

  const showSections = query.trim().length > 0 && directoryOnly.length > 0;

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const done = (): void => {
    if (selected.size === 0) {
      onClose();
      return;
    }
    const ids = [...selected];
    const picked = ids
      .map((id) => rowsById.get(id))
      .filter((c): c is ShareProfileContact => c !== undefined);
    const maybe = onShare?.(picked);
    // Legacy void handler → close immediately (old behaviour). A Promise handler
    // → show inline progress + result and auto-close on success.
    if (maybe !== undefined && typeof (maybe as Promise<ShareResult>).then === "function") {
      setShareState({ kind: "sending" });
      void (maybe as Promise<ShareResult>)
        .then((result) => {
          const r = result ?? { sent: ids.length, failed: 0 };
          setShareState({ kind: "done", result: r });
          if (r.failed === 0) setTimeout(onClose, 1300);
        })
        .catch(() => setShareState({ kind: "error" }));
    } else {
      onClose();
    }
  };

  const sharing = shareState.kind === "sending";
  const shareBanner: { text: string; color: string } | null =
    shareState.kind === "sending"
      ? { text: "Sending…", color: "#B5BAC3" }
      : shareState.kind === "done"
        ? shareState.result.failed === 0
          ? {
              text:
                shareState.result.sent === 1
                  ? "Sent to 1 contact"
                  : `Sent to ${shareState.result.sent} contacts`,
              color: "#3CCB4F",
            }
          : {
              text: `Sent to ${shareState.result.sent}, ${shareState.result.failed} failed`,
              color: "#FFB020",
            }
        : shareState.kind === "error"
          ? { text: "Couldn't send — try again", color: "#FF5353" }
          : null;

  // `AnchoredPopover` owns positioning (below the button, flips up when no room,
  // viewport-clamped), outside-click/Escape dismissal, and — via `trapFocus` —
  // keeps the contact-search input typable even though the panel is body-portaled
  // out of the parent Chat Info modal's focus trap. It measures the anchor ref on
  // OPEN, so the popover appears on the FIRST click (no stale-rect round-trip,
  // which was the old manual-anchor two-click bug).
  return (
    <AnchoredPopover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      placement="bottom"
      align="end"
      trapFocus
    >
      <View className="flex flex-col w-[360px] max-h-[440px] rounded-2xl bg-[#15171C] border border-[#22242A] shadow-2xl overflow-hidden">
        {/* Header */}
        <View className="flex-row items-center justify-between px-4 pt-4 pb-3">
          <Pressable accessibilityRole="button" onPress={onClose} className="active:opacity-70">
            <Text className="text-[14px] text-[#F5F6F8]">Cancel</Text>
          </Pressable>
          <Text className="text-[16px] font-bold text-white">{title}</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ disabled: selected.size === 0 || sharing }}
            disabled={selected.size === 0 || sharing}
            onPress={done}
            className="active:opacity-70"
          >
            <Text
              className={`text-[14px] ${
                selected.size === 0 || sharing ? "text-[#537EFF]/40" : "text-[#537EFF]"
              }`}
            >
              {sharing ? "Sending…" : doneLabel}
            </Text>
          </Pressable>
        </View>

        {/* Inline share result — the chat room's toast is a no-op, so Share
            confirms here (mirrors the "Copied!" row). */}
        {shareBanner !== null ? (
          <View className="px-4 pb-2">
            <Text className="text-[13px]" style={{ color: shareBanner.color }}>
              {shareBanner.text}
            </Text>
          </View>
        ) : null}

        {/* Copy profile link */}
        <View className="px-4">
          <Pressable
            accessibilityRole="button"
            onPress={handleCopy}
            className="flex-row items-center gap-3 h-9 px-3 rounded-[8px] bg-[#222429] active:opacity-80"
          >
            {copied ? (
              <Check size={16} strokeWidth={2} color="#3CCB4F" />
            ) : (
              <Link2 size={16} strokeWidth={1.75} color="#B5BAC3" />
            )}
            <Text className={`text-[14px] ${copied ? "text-[#3CCB4F]" : "text-[#F5F6F8]"}`}>
              {copied ? "Copied!" : copyLinkLabel}
            </Text>
          </Pressable>
        </View>

        {/* Search contacts */}
        <View className="px-4 pt-3 pb-1">
          <View className="flex-row items-center gap-2 h-[38px] px-3 rounded-[8px] bg-[#15171C] border border-[#2F333B]">
            <Search size={18} strokeWidth={1.75} color="#B5BAC3" />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder={onSearchUsers !== undefined ? "Search people or contacts" : "Search contacts"}
              placeholderTextColor="#B5BAC3"
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
              className="flex-1 text-[16px] text-white"
              style={INPUT_OUTLINE_RESET}
            />
            {query.length > 0 ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Clear search"
                onPress={() => setQuery("")}
                className="w-[18px] h-[18px] items-center justify-center"
              >
                <X size={14} strokeWidth={2} color="#B5BAC3" />
              </Pressable>
            ) : null}
          </View>
        </View>

        {/* Contacts list */}
        <ScrollView className="px-4 pt-1 pb-3" keyboardShouldPersistTaps="handled">
          {searchError !== null ? (
            <Text className="py-3 text-center text-[13px] text-[#FF5353]">{searchError}</Text>
          ) : null}

          {showSections ? (
            // Query active with directory matches → two labelled buckets.
            <>
              {localFiltered.length > 0 ? (
                <>
                  <SectionLabel text="YOUR CHATS" />
                  {localFiltered.map((c) => (
                    <ContactRow
                      key={c.id}
                      contact={c}
                      checked={selected.has(c.id)}
                      onToggle={() => toggle(c.id)}
                    />
                  ))}
                </>
              ) : null}
              <SectionLabel text="ON PING" />
              {directoryOnly.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  checked={selected.has(c.id)}
                  onToggle={() => toggle(c.id)}
                />
              ))}
            </>
          ) : localFiltered.length > 0 ? (
            localFiltered.map((c) => (
              <ContactRow
                key={c.id}
                contact={c}
                checked={selected.has(c.id)}
                onToggle={() => toggle(c.id)}
              />
            ))
          ) : searching ? (
            <View className="py-6 items-center">
              <ActivityIndicator size="small" color="#537EFF" />
            </View>
          ) : (
            <Text className="py-6 text-center text-[14px] text-[#6B7280]">
              {query.trim().length > 0
                ? onSearchUsers !== undefined
                  ? "No one matches that search"
                  : "No contacts found"
                : "No contacts found"}
            </Text>
          )}

          {/* Directory still loading while local matches already render. */}
          {searching && (localFiltered.length > 0 || directoryOnly.length > 0) ? (
            <View className="py-3 items-center">
              <ActivityIndicator size="small" color="#537EFF" />
            </View>
          ) : null}
        </ScrollView>
      </View>
    </AnchoredPopover>
  );
}

/** Section header (Figma: 12/700 muted). */
function SectionLabel({ text }: { text: string }): JSX.Element {
  return (
    <Text className="px-1 pt-3 pb-1 text-[12px] font-bold text-[#6B7280]">{text}</Text>
  );
}

/** Selectable contact/person row: avatar + name (+ optional subtitle) + checkbox. */
function ContactRow({
  contact,
  checked,
  onToggle,
}: {
  contact: ShareProfileContact;
  checked: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: checked }}
      onPress={onToggle}
      className="flex-row items-center gap-3 h-11 active:opacity-80"
    >
      <ProfileAvatar name={contact.name} uri={contact.avatarUri} sizeClassName="w-8 h-8 rounded-[8px]" />
      <View className="flex-1 min-w-0">
        <Text numberOfLines={1} className="text-[16px] text-white">
          {contact.name}
        </Text>
        {contact.subtitle !== undefined && contact.subtitle.length > 0 ? (
          <Text numberOfLines={1} className="text-[12px] text-[#B5BAC3]">
            {contact.subtitle}
          </Text>
        ) : null}
      </View>
      <View
        className={`w-5 h-5 rounded-[6px] items-center justify-center border ${
          checked ? "bg-[#537EFF] border-[#537EFF]" : "bg-[#15171C] border-[#2F333B]"
        }`}
      >
        {checked ? <Check size={14} strokeWidth={3} color="#FFFFFF" /> : null}
      </View>
    </Pressable>
  );
}

// Web-only focus-outline reset (inline escape hatch — not a valid RN style key).
const INPUT_OUTLINE_RESET = { outlineStyle: "none" } as never;
